// src/services/stakeholderService.js
import api from '../api/apiClient';

/**
 * Service for managing stakeholders in Atelier 3
 */
const stakeholderService = {
  /**
   * Get all stakeholders for an analysis
   * @param {string} analysisId - The ID of the analysis
   * @returns {Promise<Object>} - Response with stakeholders data
   */
  getStakeholders: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/atelier3/stakeholders`);
      return response;
    } catch (error) {
      console.error('Error getting stakeholders:', error);
      return { success: false, message: 'Erreur lors de la récupération des parties prenantes', error: error.message };
    }
  },

  // REMOVED: createStakeholder, updateStakeholder, deleteStakeholder — never called by any component, batch save used instead

  /**
   * Save all stakeholders for an analysis
   * @param {string} analysisId - The ID of the analysis
   * @param {Array} stakeholders - Array of stakeholders to save
   * @returns {Promise<Object>} - Response with saved stakeholders
   */
  saveStakeholders: async (analysisId, stakeholders, radarImageData = null) => {
    try {
      console.log('Saving stakeholders to:', `/analyses/${analysisId}/atelier3/stakeholders/batch`);
      console.log('Stakeholders data:', stakeholders);

      const payload = { stakeholders };
      if (radarImageData) {
        payload.radarImageData = radarImageData;
        console.log('Including radar image data in stakeholder save');
      }

      const response = await api.post(`/analyses/${analysisId}/atelier3/stakeholders/batch`, payload);

      console.log('Save response:', response);
      return response;
    } catch (error) {
      console.error('Error saving stakeholders:', error);
      return { success: false, message: 'Erreur lors de l\'enregistrement des parties prenantes', error: error.message };
    }
  },

  /**
   * Get threshold values for stakeholder threat zones
   * @param {string} analysisId - The ID of the analysis
   * @returns {Promise<Object>} - Response with threshold values
   */
  getThresholds: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/atelier3/stakeholders/thresholds`);
      return response;
    } catch (error) {
      console.error('Error getting thresholds:', error);
      return {
        success: false,
        message: 'Erreur lors de la récupération des seuils',
        error: error.message,
        data: {
          danger: 3,
          control: 1.5,
          watch: 0.5
        }
      };
    }
  },

  /**
   * Update threshold values for stakeholder threat zones
   * @param {string} analysisId - The ID of the analysis
   * @param {Object} thresholds - The threshold values (danger, control, watch)
   * @returns {Promise<Object>} - Response with updated thresholds
   */
  updateThresholds: async (analysisId, thresholds) => {
    try {
      const response = await api.post(`/analyses/${analysisId}/atelier3/stakeholders/thresholds`, thresholds);
      return response;
    } catch (error) {
      console.error('Error updating thresholds:', error);
      return { success: false, message: 'Erreur lors de la mise à jour des seuils', error: error.message };
    }
  },

  /**
   * Get saved stakeholder radar visualization image for an analysis
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Object>} - Response with radar image data
   */
  getStakeholderRadarImage: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/atelier3/stakeholders/radar-image`);
      return response;
    } catch (error) {
      console.error('Error getting stakeholder radar image:', error);
      return { success: false, message: 'Erreur lors de la récupération de l\'image du radar', error: error.message };
    }
  }
};

export default stakeholderService;
